import { useState } from 'react';
import type { Order } from '@/services/orders';
import { quotesApi } from '@/services/quotes';
import { Card } from './Card';
import { formatMinutes } from './constants';

interface PrintJobCardProps {
  readonly order: Order;
  readonly highlighted?: boolean;
}

export const PrintJobCard = ({ order, highlighted }: PrintJobCardProps) => {
  const job = order.printJob;
  const [downloading, setDownloading] = useState(false);

  if (!job) return null;

  const material = job.materialId ?? order.materialId;
  const jobColor = job.color ?? order.color;
  const canDownload = order.quoteId && job.fileKey;

  const handleViewFile = async () => {
    if (!order.quoteId || downloading) return;
    setDownloading(true);
    try {
      const url = await quotesApi.getDownloadUrl(order.quoteId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      // Silently fail — URL may have expired or file may not be available
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card highlighted={highlighted}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ fontSize: '14px', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)' }}>
          Print job
        </div>
        {canDownload && (
          <span
            style={{
              fontSize: '12px',
              color: 'var(--text-accent)',
              cursor: downloading ? 'wait' : 'pointer',
              opacity: downloading ? 0.6 : 1,
            }}
            onClick={handleViewFile}
          >
            {downloading ? 'Loading...' : 'View model file'}
          </span>
        )}
      </div>

      {/* Product name */}
      {job.productName && (
        <div style={{ fontSize: '15px', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)' }}>
          {job.productName}
        </div>
      )}

      {/* Original file */}
      {job.originalFileName && (
        <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '1px', fontFamily: 'var(--font-mono)' }}>
          {job.originalFileName}
        </div>
      )}

      {/* Chips */}
      <div className="flex flex-wrap" style={{ gap: '5px', marginTop: '8px' }}>
        {material && <Chip label={material.toUpperCase()} bold />}
        {jobColor && <Chip label={jobColor} bold />}
        {job.dimensions && (
          <Chip
            label={`${job.dimensions.width} x ${job.dimensions.height} x ${job.dimensions.depth} cm`}
            bold
          />
        )}
      </div>

      {/* Spec grid */}
      <div style={{ height: '0.5px', backgroundColor: 'var(--border-default)', margin: '8px 0' }} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', fontSize: '13px' }}>
        {job.weightGrams !== null && <KVRow label="Weight" value={`${job.weightGrams}g`} />}
        {job.nozzleSize !== null && <KVRow label="Nozzle" value={`${job.nozzleSize}mm`} />}
        {job.layerHeight !== null && <KVRow label="Layer height" value={`${job.layerHeight}mm`} />}
        {job.infillPercent !== null && <KVRow label="Infill" value={`${job.infillPercent}%`} />}
        {job.wallCount !== null && <KVRow label="Wall count" value={String(job.wallCount)} />}
        <KVRow
          label="Supports"
          value={job.supportsRequired === null ? 'N/A' : job.supportsRequired ? 'Auto' : 'None'}
          isLast
        />
        <KVRow
          label="Adhesion"
          value={job.brimRaft ? job.brimRaft.charAt(0).toUpperCase() + job.brimRaft.slice(1) : 'None'}
          isLast
        />
      </div>

      {/* Estimate vs Actual */}
      <div className="flex" style={{ gap: '12px', marginTop: '8px' }}>
        <EstimateBox
          label="Filament (g)"
          estimated={job.estimatedFilamentGrams !== null ? `Est ${job.estimatedFilamentGrams}g` : 'Est --'}
          actual={job.actualFilamentGrams !== null ? `Actual ${job.actualFilamentGrams}g` : 'Actual --'}
        />
        <EstimateBox
          label="Print time"
          estimated={
            job.estimatedPrintTimeMinutes !== null
              ? `Est ${formatMinutes(job.estimatedPrintTimeMinutes)}`
              : 'Est --'
          }
          actual={
            job.actualPrintTimeMinutes !== null
              ? `Actual ${formatMinutes(job.actualPrintTimeMinutes)}`
              : 'Actual --'
          }
        />
      </div>
    </Card>
  );
};

// Sub-components

const Chip = ({ label, bold }: { readonly label: string; readonly bold?: boolean }) => (
  <span
    style={{
      fontSize: '11px',
      padding: '3px 9px',
      borderRadius: '16px',
      backgroundColor: 'var(--bg-surface-sunken)',
      color: bold ? 'var(--text-primary)' : 'var(--text-secondary)',
      fontWeight: bold ? 'var(--font-weight-medium)' : 'var(--font-weight-regular)',
    }}
  >
    {label}
  </span>
);

const KVRow = ({ label, value, isLast }: { readonly label: string; readonly value: string; readonly isLast?: boolean }) => (
  <div style={{ display: 'contents' }}>
    <span
      style={{
        padding: '5px 0',
        color: 'var(--text-tertiary)',
        borderBottom: isLast ? 'none' : '0.5px solid var(--border-default)',
      }}
    >
      {label}
    </span>
    <span
      style={{
        padding: '5px 0',
        textAlign: 'right',
        color: 'var(--text-primary)',
        borderBottom: isLast ? 'none' : '0.5px solid var(--border-default)',
      }}
    >
      {value}
    </span>
  </div>
);

const EstimateBox = ({
  label,
  estimated,
  actual,
}: {
  readonly label: string;
  readonly estimated: string;
  readonly actual: string;
}) => (
  <div
    style={{
      flex: 1,
      backgroundColor: 'var(--bg-surface-sunken)',
      borderRadius: 'var(--radius-md)',
      padding: '8px 10px',
    }}
  >
    <div style={{ color: 'var(--text-tertiary)', fontSize: '11px', marginBottom: '2px' }}>
      {label}
    </div>
    <div className="flex justify-between" style={{ fontSize: '12px' }}>
      <span style={{ color: 'var(--text-secondary)' }}>{estimated}</span>
      <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)' }}>{actual}</span>
    </div>
  </div>
);
